/**
 * RunningProgressView - In-progress execution detail.
 *
 * Renders the running-specific UI that replaces the Output Files section
 * while an execution is pending/running:
 *  - progress ring (queries done / total)
 *  - elapsed time
 *  - current resource being processed
 *  - resource pills (done/failed/running/pending)
 *  - live log
 *
 * Output files do not exist yet while a run is in progress, so this view
 * stands in for the dataset output groups. The surrounding ExecutionDetail still
 * shows the run header (date/status/duration) and ExecutionStats; this
 * component is just the body that swaps in.
 */
import { cn } from '@/lib/utils';
import { useElapsedTime } from '@/hooks/useExecutionPolling';
import { ExecutionLogPane } from './ExecutionLogPane';
import type { ConnectorExecution, ExecutionLogEntry } from '@/types/connectors';

interface Props {
  execution: ConnectorExecution;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m <= 0) return `${s}s`;
  return `${m}m ${s}s`;
}

type ResourceStatus = 'done' | 'failed' | 'running' | 'pending';

function resourceStatusMap(
  resourceKeys: string[] | null | undefined,
  log: ExecutionLogEntry[] | null | undefined,
  current: string | null | undefined,
): Record<string, ResourceStatus> {
  const out: Record<string, ResourceStatus> = {};
  for (const k of resourceKeys ?? []) out[k] = 'pending';
  for (const entry of log ?? []) {
    // Ensure the resource appears as a pill even if it isn't in
    // resourceKeys (e.g. meta_ads object resources whose resource_key
    // isn't predictable at launch time). See #181.
    if (!(entry.resource in out)) out[entry.resource] = 'pending';

    if (entry.status === 'success' || entry.status === 'empty') {
      // Empty is a terminal 'done' state — the worker finished the
      // resource, it just had zero rows.
      out[entry.resource] = 'done';
    } else if (entry.status === 'failed') {
      out[entry.resource] = 'failed';
    } else if (entry.status === 'started') {
      if (out[entry.resource] !== 'done' && out[entry.resource] !== 'failed') {
        out[entry.resource] = 'running';
      }
    }
    // 'skipped' leaves the existing state (pending) — the resource was
    // intentionally not attempted on this run.
  }
  if (current && !(current in out)) out[current] = 'pending';
  if (current && out[current] !== 'done' && out[current] !== 'failed') {
    out[current] = 'running';
  }
  return out;
}

export function RunningProgressView({ execution }: Props) {
  const elapsed = useElapsedTime(execution.started_at);
  const done = (execution.queries_successful ?? 0) + (execution.queries_failed ?? 0);
  // Without a real denominator we cannot know the percent. Falling back to
  // `total = done` pegs the ring at a misleading 100% for the entire run
  // (#224), so render an indeterminate state instead (percent === null).
  const hasDenominator =
    execution.expected_resources != null && execution.expected_resources > 0;
  const total = hasDenominator ? execution.expected_resources! : done;
  const percent =
    hasDenominator && total > 0
      ? Math.min(100, Math.round((done / total) * 100))
      : null;
  const ringDeg = (percent ?? 0) * 3.6;

  const statuses = resourceStatusMap(
    execution.resource_keys,
    execution.log,
    execution.current_resource,
  );
  // True ordered union: launch-time keys first, then any new resources
  // observed in the log, then current_resource. Prevents the pill list
  // from omitting log-only resources (#181) without disturbing the
  // stable resource_keys ordering for connectors that pre-declare them.
  const allResources = Array.from(
    new Set<string>([
      ...(execution.resource_keys ?? []),
      ...((execution.log ?? []).map((e) => e.resource)),
      ...(execution.current_resource ? [execution.current_resource] : []),
    ]),
  );

  return (
    <div className="space-y-6" data-testid="running-progress-view">
      <div className="flex items-start gap-6">
        <div
          className="relative h-24 w-24 rounded-full flex items-center justify-center"
          style={{ background: `conic-gradient(var(--color-primary) ${ringDeg}deg, var(--color-muted) 0deg)` }}
          data-testid="progress-ring"
        >
          <div
            className={cn(
              'h-[72px] w-[72px] rounded-full bg-card flex flex-col items-center justify-center',
              percent === null && 'animate-pulse',
            )}
          >
            <span className="text-xl font-semibold" data-testid="progress-percent">
              {percent === null ? '…' : `${percent}%`}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {done} / {hasDenominator ? total : '?'}
            </span>
          </div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="text-sm text-muted-foreground">Elapsed</div>
          <div className="text-2xl font-semibold" data-testid="elapsed-time">
            {formatElapsed(elapsed)}
          </div>
          <div className="text-xs text-muted-foreground">
            Current:{' '}
            <span className="font-medium text-foreground" data-testid="current-resource">
              {execution.current_resource ?? '—'}
            </span>
          </div>
          <div className="mt-2 w-full h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={cn('h-full bg-info transition-all', percent === null && 'animate-pulse')}
              style={{ width: percent === null ? '33%' : `${percent}%` }}
            />
          </div>
        </div>
      </div>

      {allResources.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground uppercase mb-2">Resources</div>
          <div className="flex flex-wrap gap-2">
            {allResources.map((r) => {
              const st = statuses[r] ?? 'pending';
              return (
                <span
                  key={r}
                  data-testid={`resource-pill-${r}`}
                  data-status={st}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border',
                    st === 'done' && 'bg-success/10 text-success border-success',
                    st === 'failed' && 'bg-destructive/10 text-destructive border-destructive',
                    st === 'running' && 'bg-info/10 text-info border-info animate-pulse',
                    st === 'pending' && 'bg-muted text-muted-foreground border-transparent',
                  )}
                >
                  {r}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <ExecutionLogPane log={execution.log} title="Live log" />
    </div>
  );
}
